import axios, { AxiosError } from 'axios';
import { InsightsData } from '../types/insights';
import { validateInsightsData, isValidInsightsResponse, safeApiResponse } from './dataValidation';
import {
  PromptResponse,
  PromptVersionsResponse,
  PromptUpdateRequest,
  DiffResponse
} from '../types/prompts';
import { InventoryDatesResponse, InventoryUsersResponse } from '../types/inventory';

// API configuration with multiple fallback checks for production
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://insights-api-558553787161.us-central1.run.app';


export const fetchInsights = async (userId: string, date: string): Promise<InsightsData | null> => {
  try {
    const response = await axios.get(`${API_BASE_URL}/insights`, {
      params: {
        user_id: userId,
        date: date
      }
    });
    
    // Unwrap data if it's wrapped
    const rawData = response.data.data || response.data;
    
    // Validate response structure
    if (!isValidInsightsResponse(rawData)) {
      console.warn('Invalid insights response structure:', rawData);
      return null;
    }
    
    // Validate and sanitize the data
    return safeApiResponse(rawData, validateInsightsData, {
      date: date,
      user_name: 'Unknown User',
      metrics: { go2_score: 0, engagement_score: 0 },
      insights: []
    });
  } catch (error) {
    // Handle 404 responses as no data found - don't throw
    if (error instanceof AxiosError && error.response?.status === 404) {
      console.info(`No insights found for user ${userId} on ${date}`);
      return null;
    }

    console.error('Error fetching insights:', error);
    // Make sure error has response property for retry logic
    if (error instanceof AxiosError) {
      throw error; // AxiosError already has response property
    }
    // Wrap non-axios errors
    const errorMessage = error instanceof Error ? error.message : 'Failed to fetch insights';
    throw new Error(errorMessage);
  }
};

export const fetchPrompt = async (name: string): Promise<PromptResponse> => {
  try {
    const response = await axios.get<PromptResponse>(`${API_BASE_URL}/prompts/${name}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching prompt ${name}:`, error);
    throw error;
  }
};

export const updatePrompt = async (name: string, content: string): Promise<{ message: string; metadata: any }> => {
  try {
    const request: PromptUpdateRequest = { content };
    const response = await axios.put(`${API_BASE_URL}/prompts/${name}`, request);
    return response.data;
  } catch (error) {
    console.error(`Error updating prompt ${name}:`, error);
    throw error;
  }
};

export const fetchPromptVersions = async (name: string): Promise<PromptVersionsResponse> => {
  try {
    const response = await axios.get<PromptVersionsResponse>(`${API_BASE_URL}/prompts/${name}/versions`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching versions for prompt ${name}:`, error);
    throw error;
  }
};

export const restorePromptVersion = async (name: string, version: string): Promise<{ message: string; metadata: any }> => {
  try {
    const response = await axios.post(`${API_BASE_URL}/prompts/${name}/restore/${version}`);
    return response.data;
  } catch (error) {
    console.error(`Error restoring version ${version} of prompt ${name}:`, error);
    throw error;
  }
};

export const fetchPromptDiff = async (name: string, fromVersion: string, toVersion: string = 'latest'): Promise<DiffResponse> => {
  try {
    const response = await axios.get<DiffResponse>(`${API_BASE_URL}/prompts/${name}/diff`, {
      params: {
        from_version: fromVersion,
        to_version: toVersion
      }
    });
    return response.data;
  } catch (error) {
    console.error(`Error fetching diff for prompt ${name} from ${fromVersion} to ${toVersion}:`, error);
    throw error;
  }
};

// Markdown Report API functions

export const fetchWorkblockInsights = async (userId: string, date: string): Promise<string> => {
  try {
    const response = await axios.get(`${API_BASE_URL}/insights/workblock/${userId}`, {
      params: { date }
    });
    return response.data;
  } catch (error) {
    console.error(`Error fetching workblock insights for user ${userId} on ${date}:`, error);
    throw error;
  }
};

export const fetchChatInsights = async (userId: string, date: string): Promise<string> => {
  try {
    const response = await axios.get(`${API_BASE_URL}/insights/chat/${userId}`, {
      params: { date }
    });
    return response.data;
  } catch (error) {
    console.error(`Error fetching chat insights for user ${userId} on ${date}:`, error);
    throw error;
  }
};

export const fetchGo2Brief = async (userId: string, date: string): Promise<string> => {
  try {
    const response = await axios.get(`${API_BASE_URL}/insights/go2-brief/${userId}`, {
      params: { date }
    });
    return response.data;
  } catch (error) {
    console.error(`Error fetching Go2 brief for user ${userId} on ${date}:`, error);
    throw error;
  }
};

// Inventory API functions

export const fetchInventoryDates = async (): Promise<InventoryDatesResponse> => {
  try {
    const response = await axios.get<InventoryDatesResponse>(`${API_BASE_URL}/inventory/dates`);
    return response.data;
  } catch (error) {
    console.error('Error fetching available dates:', error);
    throw error;
  }
};

export const fetchInventoryUsers = async (date: string): Promise<InventoryUsersResponse> => {
  try {
    const response = await axios.get<InventoryUsersResponse>(`${API_BASE_URL}/inventory/users`, {
      params: { date }
    });
    return response.data;
  } catch (error) {
    console.error(`Error fetching users for date ${date}:`, error);
    throw error;
  }
};

export const fetchWorkblockReport = async (userId: string, date: string): Promise<string> => {
  try {
    const response = await axios.get(`${API_BASE_URL}/reports/workblock/${userId}`, {
      params: { date }
    });
    return response.data;
  } catch (error) {
    console.error(`Error fetching workblock report for user ${userId} on ${date}:`, error);
    throw error;
  }
};

export const fetchChatHistoryReport = async (userId: string, date: string): Promise<string> => {
  try {
    const response = await axios.get(`${API_BASE_URL}/reports/chat/${userId}`, {
      params: { date }
    });
    return response.data;
  } catch (error) {
    console.error(`Error fetching chat history report for user ${userId} on ${date}:`, error);
    throw error;
  }
};
